import CheckIcon from "@mui/icons-material/Check";
import {
  Box,
  Stack,
  Step,
  StepButton,
  StepLabel,
  Stepper,
} from "@mui/material";
import { StepIconProps } from "@mui/material/StepIcon";
import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import { brand, gray } from "../../../theme/themePrimitives";
import { ConfirmAndExecute } from "./components/ConfirmAndExecute/ConfirmAndExecute";
import { PrepareAndUpload } from "./components/PrepareAndUpload/PrepareAndUpload";
import { ReviewChanges } from "./components/ReviewChanges/ReviewChanges";
import { ValidationOutcome } from "./components/ValidationOutcome/ValidationOutcome";
import { useBulkUserImport } from "./useBulkUserImport";

const STEPS = [
  "Prepare & Upload",
  "Validation Outcome",
  "Review Changes",
  "Confirm & Execute",
];

const LAST_STEP = STEPS.length - 1;

const ICON_WRAPPER_SIZE = 24;

const iconWrapperSx = {
  width: ICON_WRAPPER_SIZE,
  height: ICON_WRAPPER_SIZE,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const BulkStepIcon: React.FC<StepIconProps> = ({ active, completed }) => {
  if (completed) {
    return (
      <Box sx={iconWrapperSx}>
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            bgcolor: brand[600],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CheckIcon sx={{ fontSize: 14, color: "#fff" }} />
        </Box>
      </Box>
    );
  }

  if (active) {
    return (
      <Box sx={iconWrapperSx}>
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            bgcolor: brand[600],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 0 0 5px ${brand[100]}`,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "#fff",
            }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={iconWrapperSx}>
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          border: `1.5px solid ${gray[300]}`,
          bgcolor: "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: gray[300],
          }}
        />
      </Box>
    </Box>
  );
};

export const BulkUserActions: React.FC = () => {
  const location = useLocation();
  const bulk = useBulkUserImport();
  const { activeStep, goToStep, reset } = bulk;

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    reset();
  }, [location.key, reset]);

  usePageMetadata({
    title: "Bulk user create & update",
    breadcrumbs: [
      {
        label: "User management",
        path: `/portal-configuration`,
      },
      {
        label: "Bulk user actions",
      },
    ],
  });

  const isOnLastStep = activeStep === LAST_STEP;

  return (
    <Stack spacing={3}>
      {activeStep !== 3 && (
        <Stepper
          nonLinear
          alternativeLabel
          activeStep={activeStep}
          sx={{
            bgcolor: "background.paper",
            borderRadius: 2,
            px: 4,
            py: 2.5,
            pointerEvents: isOnLastStep ? "none" : undefined,

            "& .MuiStepConnector-root": {
              left: "calc(-50% + 12px)",
              right: "calc(50% + 12px)",
            },
            "& .MuiStepLabel-label": {
              mt: "6px !important",
              fontSize: 14,
              color: gray[500],
            },
            "& .MuiStepLabel-label.Mui-active": {
              color: brand[600],
              fontWeight: 600,
            },
            "& .MuiStepLabel-label.Mui-completed": {
              color: gray[500],
              opacity: 1,
            },
          }}
        >
          {STEPS.map((label, index) => (
            <Step key={label} completed={index < activeStep}>
              <StepButton
                disableRipple
                onClick={() => {
                  if (!isOnLastStep && index < activeStep) {
                    goToStep(index);
                  }
                }}
                sx={{
                  cursor:
                    index < activeStep && !isOnLastStep ? "pointer" : "default",
                  "& .MuiStepButton-touchRipple": { display: "none" },
                }}
              >
                <StepLabel slots={{ stepIcon: BulkStepIcon }}>
                  {label}
                </StepLabel>
              </StepButton>
            </Step>
          ))}
        </Stepper>
      )}
      {(activeStep === 0 || activeStep === 1) && (
        <PrepareAndUpload
          file={bulk.file}
          setFile={bulk.setFile}
          jobId={bulk.jobId}
          hasRunValidation={bulk.hasRunValidation}
          uploadFile={bulk.uploadFile}
          runValidation={bulk.runValidation}
          isUploading={bulk.isUploading}
          isValidating={bulk.isValidating}
          awaitingValidation={bulk.awaitingValidation}
          downloadTemplate={bulk.downloadTemplate}
          downloadingTemplate={bulk.downloadingTemplate}
        />
      )}
      {activeStep === 1 && (
        <ValidationOutcome
          validation={bulk.validation}
          isValidating={bulk.isValidating}
          autoFix={bulk.autoFix}
          setAutoFix={bulk.setAutoFix}
          downloadValidationLog={bulk.downloadValidationLog}
          goToStep={bulk.goToStep}
        />
      )}
      {activeStep === 2 && (
        <ReviewChanges
          jobId={bulk.jobId}
          includedSections={bulk.includedSections}
          toggleSection={bulk.toggleSection}
          execute={bulk.execute}
          isExecuting={bulk.isExecuting}
          goToStep={bulk.goToStep}
        />
      )}
      {activeStep === 3 && <ConfirmAndExecute result={bulk.executeResult} />}
    </Stack>
  );
};
